import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { Component, PropsWithChildren, ReactNode } from 'react';

import { ErrorPage } from './error-page';

type BoundaryProps = PropsWithChildren<{
  onReset: () => void;
}>;

type BoundaryState = {
  hasError: boolean;
};

class RenderErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  private handleRetry = () => {
    this.props.onReset();
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorPage onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

/**
 * Without a boundary above them, `useSuspenseQuery` consumers rethrow on every
 * render attempt: React re-renders, the query refetches, it throws again. A
 * single failing request becomes a request flood at render speed.
 *
 * `QueryErrorResetBoundary` is what makes the retry meaningful — it clears the
 * cached rejection so the remounted children refetch instead of throwing the
 * stored error straight back.
 */
export const AppErrorBoundary = ({ children }: PropsWithChildren) => (
  <QueryErrorResetBoundary>
    {({ reset }) => <RenderErrorBoundary onReset={reset}>{children}</RenderErrorBoundary>}
  </QueryErrorResetBoundary>
);
